import { useTranslation } from 'react-i18next';

import type {
  IEmailABTestSettings,
  IMailrailSingleSendMailing,
  MailrailBaseEmailCampaign,
  MailrailSingleSendEmailCampaign,
  TEmailABTestVariation,
  TEmailCampaignMailing,
} from '@feathr/blackbox';
import { MailrailSingleSendMailing } from '@feathr/blackbox';
import { useFlags } from '@feathr/extender/state';
import {
  getEmailABTest,
  getEmailABTestVariationMailing,
  isEmailABTestAvailable,
} from '@feathr/extender/utils/emailABTest';

import { useEphemeralMailing } from '../useEphemeralMailing';

// Version A is the campaign itself; later versions exist only once the backend has created them.
export interface IGetEmailABTestVersionMailing {
  (variation: 'a'): TEmailCampaignMailing;
  (variation: TEmailABTestVariation): TEmailCampaignMailing | undefined;
}

export interface IUseMailrailABTestResult {
  abTest: IEmailABTestSettings;
  abTestPublishErrors: string[];
  copyVersionA: () => Promise<void>;
  getVersionMailing: IGetEmailABTestVersionMailing;
  isABTestAvailable: boolean;
  setABTest: (abTest: IEmailABTestSettings) => void;
}

export function useMailrailABTest(campaign: MailrailBaseEmailCampaign): IUseMailrailABTestResult {
  const flags = useFlags();
  const { t } = useTranslation();
  const singleSend = campaign as MailrailSingleSendEmailCampaign;
  const abTest = getEmailABTest(singleSend);
  const isABTestAvailable = isEmailABTestAvailable(flags, campaign);

  const variantMailing = campaign.isMailrailSingleSend
    ? getEmailABTestVariationMailing(singleSend, 'b')
    : undefined;
  const versionBMailing = useEphemeralMailing(
    MailrailSingleSendMailing,
    variantMailing,
    variantMailing?.mailing_id,
    handleChangeVersionB,
    { isSeedDirty: singleSend.isAttributeDirty('mailings') },
  );

  function handleChangeVersionB(patch: Partial<IMailrailSingleSendMailing>): void {
    if (variantMailing) {
      singleSend.setVariantMailing(variantMailing.mailing_id, patch);
    }
  }

  function getVersionMailing(variation: 'a'): TEmailCampaignMailing;
  function getVersionMailing(variation: TEmailABTestVariation): TEmailCampaignMailing | undefined;
  function getVersionMailing(variation: TEmailABTestVariation): TEmailCampaignMailing | undefined {
    return variation === 'a' ? campaign : versionBMailing;
  }

  function setABTest(next: IEmailABTestSettings): void {
    singleSend.set({ ab_test: next });
  }

  async function copyVersionA(): Promise<void> {
    await singleSend.copyVariation('a', 'b');
  }

  // The backend doesn't send or check A/B campaigns on publish yet, so an enabled test would go out as a regular single send.
  const abTestPublishErrors =
    isABTestAvailable && abTest.is_enabled
      ? [t("A/B tests can't be sent yet. Turn off the A/B test to publish.")]
      : [];

  return {
    abTest,
    abTestPublishErrors,
    copyVersionA,
    getVersionMailing,
    isABTestAvailable,
    setABTest,
  };
}
